import {
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
} from "firebase/auth";
import {
  doc,
  setDoc,
  getDoc,
  getDocs,
  collection,
  query,
  where,
  deleteDoc,
  updateDoc,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { auth, db, functions } from "./firebase";

// User roles for school users
export const SCHOOL_USER_ROLES = {
  SCHOOL_ADMIN: "school-admin",
  SCHOOL_EDITOR: "school-editor", 
  SCHOOL_VIEWER: "school-viewer",
};

// Get all users for a specific school
export const getSchoolUsers = async (schoolId) => {
  try {
    // Get all users and filter those with access to this school
    const allUsersSnapshot = await getDocs(collection(db, "users"));
    
    const users = [];
    allUsersSnapshot.forEach((doc) => {
      const userData = doc.data();
      
      // Check if user has access to this school
      // Support both legacy (schoolId) and new (schools array) structure
      const hasLegacyAccess = userData.schoolId === schoolId;
      const hasArrayAccess = userData.schools?.some(s => s.schoolId === schoolId);
      
      if (hasLegacyAccess || hasArrayAccess) {
        // Get the role for this specific school
        let schoolRole = userData.schoolRole; // default to legacy role
        if (userData.schools) {
          const schoolData = userData.schools.find(s => s.schoolId === schoolId);
          if (schoolData) {
            schoolRole = schoolData.role;
          }
        }
        
        users.push({
          id: doc.id,
          ...userData,
          schoolRole: schoolRole // Override with school-specific role
        });
      }
    });
    
    return users;
  } catch (error) {
    console.error("Error fetching school users:", error);
    throw error;
  }
};

// Create an invitation for a new user or add school to existing user
export const createUserInvitation = async (schoolId, email, role, invitedBy) => {
  try {
    // Check if user already exists
    const existingUserQuery = query(
      collection(db, "users"),
      where("email", "==", email)
    );
    const existingUserSnapshot = await getDocs(existingUserQuery);
    
    if (!existingUserSnapshot.empty) {
      // User exists - add this school to their access
      const existingUser = existingUserSnapshot.docs[0];
      const userData = existingUser.data();
      
      // Initialize schools array from existing data
      let schools = userData.schools || [];
      
      // If user has legacy structure but no schools array, migrate it
      if (userData.schoolId && schools.length === 0) {
        schools = [{
          schoolId: userData.schoolId,
          role: userData.schoolRole || SCHOOL_USER_ROLES.SCHOOL_VIEWER
        }];
      }
      
      // Check if user already has access to this school
      if (schools.some(s => s.schoolId === schoolId) || userData.schoolId === schoolId) {
        throw new Error("User already has access to this school");
      }
      
      // Add school to user's schools array
      const updatedSchools = [...schools, { schoolId, role }];
      await updateDoc(doc(db, "users", existingUser.id), {
        schools: updatedSchools,
        // Keep role as "school" if not already set
        role: userData.role || "school"
      });
      
      // Get school name for response
      const schoolDoc = await getDoc(doc(db, "schools", schoolId));
      const schoolName = schoolDoc.data()?.name || "Unknown School";
      
      return {
        success: true,
        existingUser: true,
        email,
        schoolName,
        message: `Added ${email} to ${schoolName}`
      };
    }
    
    // Check if invitation already exists
    const existingInviteQuery = query(
      collection(db, "userInvitations"),
      where("email", "==", email),
      where("schoolId", "==", schoolId),
      where("status", "==", "pending")
    );
    const existingInviteSnapshot = await getDocs(existingInviteQuery);
    
    if (!existingInviteSnapshot.empty) {
      throw new Error("An invitation has already been sent to this email");
    }
    
    // Create invitation token
    const invitationToken = Math.random().toString(36).substring(2) + Date.now().toString(36);
    
    // Store invitation in Firestore
    const invitationRef = await addDoc(collection(db, "userInvitations"), {
      email,
      schoolId,
      role,
      invitedBy,
      invitationToken,
      status: "pending",
      createdAt: serverTimestamp(),
      expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 days
    });
    
    // Get school name for the invitation
    const schoolDoc = await getDoc(doc(db, "schools", schoolId));
    const schoolName = schoolDoc.data()?.name || "Unknown School";
    
    // Get inviter name
    const inviterDoc = await getDoc(doc(db, "users", invitedBy));
    const inviterName = inviterDoc.data()?.displayName || inviterDoc.data()?.email || "An administrator";
    
    const inviteUrl = `${window.location.origin}?token=${invitationToken}`;
    
    // Send invitation email via Cloud Function
    try {
      const sendInvitationEmail = httpsCallable(functions, "sendUserInvitationEmail");
      await sendInvitationEmail({
        email,
        schoolName,
        inviteUrl,
        inviterName,
        role
      });
      console.log("Invitation email sent successfully");
    } catch (emailError) {
      console.error("Failed to send invitation email:", emailError);
      // Continue even if email fails - user can still get the link
    }
    
    // Return invitation details
    return {
      invitationId: invitationRef.id,
      invitationToken,
      email,
      schoolName,
      inviteUrl,
    };
  } catch (error) {
    console.error("Error creating user invitation:", error);
    throw error;
  }
};

// Accept an invitation and create user account
export const acceptInvitation = async (invitationToken, password, displayName) => {
  try {
    // Find the invitation
    const inviteQuery = query(
      collection(db, "userInvitations"),
      where("invitationToken", "==", invitationToken),
      where("status", "==", "pending")
    );
    const inviteSnapshot = await getDocs(inviteQuery);
    
    if (inviteSnapshot.empty) {
      throw new Error("Invalid or expired invitation");
    }
    
    const inviteDoc = inviteSnapshot.docs[0];
    const inviteData = inviteDoc.data();
    
    // Check if invitation is expired
    if (inviteData.expiresAt.toDate() < new Date()) {
      await updateDoc(doc(db, "userInvitations", inviteDoc.id), {
        status: "expired",
      });
      throw new Error("This invitation has expired");
    }
    
    // Create the user account
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      inviteData.email,
      password
    );
    
    // Create user document in Firestore with schools array
    await setDoc(doc(db, "users", userCredential.user.uid), {
      email: inviteData.email,
      displayName: displayName || inviteData.email.split("@")[0],
      role: "school",
      schools: [
        {
          schoolId: inviteData.schoolId,
          role: inviteData.role
        }
      ],
      // Keep legacy fields for backward compatibility
      schoolRole: inviteData.role,
      schoolId: inviteData.schoolId,
      createdAt: serverTimestamp(),
      invitedBy: inviteData.invitedBy,
    });
    
    // Mark invitation as accepted
    await updateDoc(doc(db, "userInvitations", inviteDoc.id), {
      status: "accepted",
      acceptedAt: serverTimestamp(),
      userId: userCredential.user.uid,
    });
    
    return userCredential.user;
  } catch (error) {
    console.error("Error accepting invitation:", error);
    throw error;
  }
};

// Update user role within a school
export const updateUserRole = async (userId, newRole) => {
  try {
    await updateDoc(doc(db, "users", userId), {
      schoolRole: newRole,
      updatedAt: serverTimestamp(),
    });
    
    return true;
  } catch (error) {
    console.error("Error updating user role:", error);
    throw error;
  }
};

// Remove user from school
export const removeUserFromSchool = async (userId, schoolId) => {
  try {
    // Get the user being removed
    const userDocRef = doc(db, "users", userId);
    const userDoc = await getDoc(userDocRef);
    
    if (!userDoc.exists()) {
      throw new Error("User not found");
    }
    
    const userData = userDoc.data();
    
    // Check if user has access to this school
    const schools = userData.schools || [];
    const schoolIndex = schools.findIndex(s => s.schoolId === schoolId);
    
    // Also check legacy structure
    const hasLegacyAccess = userData.schoolId === schoolId;
    
    if (schoolIndex === -1 && !hasLegacyAccess) {
      throw new Error("User does not have access to this school");
    }
    
    // Get the user's role for this specific school
    let userRoleForSchool = userData.schoolRole; // default to legacy role
    if (schoolIndex !== -1) {
      userRoleForSchool = schools[schoolIndex].role;
    }
    
    // Check if this is the last admin for the school
    // Need to check all users who have admin access to this school
    const allUsersSnapshot = await getDocs(collection(db, "users"));
    let adminCount = 0;
    
    allUsersSnapshot.forEach((doc) => {
      const data = doc.data();
      // Check legacy structure
      if (data.schoolId === schoolId && data.schoolRole === SCHOOL_USER_ROLES.SCHOOL_ADMIN) {
        adminCount++;
      }
      // Check new structure
      if (data.schools) {
        const schoolData = data.schools.find(s => s.schoolId === schoolId);
        if (schoolData && schoolData.role === SCHOOL_USER_ROLES.SCHOOL_ADMIN) {
          adminCount++;
        }
      }
    });
    
    if (adminCount === 1 && userRoleForSchool === SCHOOL_USER_ROLES.SCHOOL_ADMIN) {
      throw new Error("Cannot remove the last admin from the school");
    }
    
    // If user has multiple schools, just remove this school from their access
    if (schools.length > 1 || (schools.length === 1 && !hasLegacyAccess)) {
      const updatedSchools = schools.filter(s => s.schoolId !== schoolId);
      
      if (updatedSchools.length > 0) {
        // Update user document with remaining schools
        await updateDoc(userDocRef, {
          schools: updatedSchools,
          // If removing the legacy school, clear legacy fields
          ...(hasLegacyAccess && {
            schoolId: updatedSchools[0].schoolId,
            schoolRole: updatedSchools[0].role
          })
        });
      } else {
        // No schools left, delete the user document
        await deleteDoc(userDocRef);
      }
    } else {
      // User only has access to this one school (legacy structure), delete the document
      await deleteDoc(userDocRef);
    }
    
    return true;
  } catch (error) {
    console.error("Error removing user from school:", error);
    throw error;
  }
};

// Resend invitation email
export const resendInvitation = async (invitationId) => {
  try {
    const inviteDoc = await getDoc(doc(db, "userInvitations", invitationId));
    
    if (!inviteDoc.exists()) {
      throw new Error("Invitation not found");
    }
    
    const inviteData = inviteDoc.data();
    
    if (inviteData.status !== "pending") {
      throw new Error("Can only resend pending invitations");
    }
    
    // Update expiration date
    await updateDoc(doc(db, "userInvitations", invitationId), {
      expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 days
      resentAt: serverTimestamp(),
    });
    
    // Get school name
    const schoolDoc = await getDoc(doc(db, "schools", inviteData.schoolId));
    const schoolName = schoolDoc.data()?.name || "Unknown School";
    
    // Get original inviter name
    const inviterDoc = await getDoc(doc(db, "users", inviteData.invitedBy));
    const inviterName = inviterDoc.data()?.displayName || inviterDoc.data()?.email || "An administrator";
    
    const inviteUrl = `${window.location.origin}?token=${inviteData.invitationToken}`;
    
    // Resend invitation email via Cloud Function
    try {
      const sendInvitationEmail = httpsCallable(functions, "sendUserInvitationEmail");
      await sendInvitationEmail({
        email: inviteData.email,
        schoolName,
        inviteUrl,
        inviterName,
        role: inviteData.role
      });
      console.log("Invitation email resent successfully");
    } catch (emailError) {
      console.error("Failed to resend invitation email:", emailError);
      // Continue even if email fails
    }
    
    return {
      email: inviteData.email,
      schoolName,
      inviteUrl,
    };
  } catch (error) {
    console.error("Error resending invitation:", error);
    throw error;
  }
};

// Get pending invitations for a school
export const getSchoolInvitations = async (schoolId) => {
  try {
    const invitationsQuery = query(
      collection(db, "userInvitations"),
      where("schoolId", "==", schoolId),
      where("status", "==", "pending")
    );
    const snapshot = await getDocs(invitationsQuery);
    
    const invitations = [];
    snapshot.forEach((doc) => {
      const data = doc.data();
      invitations.push({
        id: doc.id,
        ...data,
        expiresAt: data.expiresAt?.toDate(),
        createdAt: data.createdAt?.toDate(),
      });
    });
    
    return invitations;
  } catch (error) {
    console.error("Error fetching school invitations:", error);
    throw error;
  }
};

// Cancel pending invitation
export const cancelInvitation = async (invitationId) => {
  try {
    await updateDoc(doc(db, "userInvitations", invitationId), {
      status: "cancelled",
      cancelledAt: serverTimestamp(),
    });
    
    return true;
  } catch (error) {
    console.error("Error cancelling invitation:", error);
    throw error;
  }
};

// Check if user has permission to manage users
export const canManageUsers = (userRole, schoolRole) => {
  return (
    userRole === "studio" ||
    schoolRole === SCHOOL_USER_ROLES.SCHOOL_ADMIN
  );
};

// Send password reset email for existing users
export const sendPasswordReset = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
    return true;
  } catch (error) {
    console.error("Error sending password reset:", error);
    throw error;
  }
};